import { DateTime, type MsSinceEpoch, NaiveDate, Epoch } from "@tento-chrono";
import { Local, type LogicalTimezone, TimezoneInfo } from "@tento-chrono";
import type { TimezonePreferences } from "@tento-lona";

/**
 * User-aware local timezone that respects timezone preferences.
 *
 * Call `LonaUserLocal.init(timezonePreferences)` during app bootstrap
 * to wire up the preference store. Before init, falls back to browser local.
 */
class LonaUserLocalTimezone implements LogicalTimezone<"local"> {
  id = "local" as const;
  private tz: Option<TimezonePreferences> = null;

  init(tz: TimezonePreferences): void {
    this.tz = tz;
  }

  get info(): TimezoneInfo {
    const preferredTz = this.tz?.peek();
    if (preferredTz) {
      return TimezoneInfo.local();
    }
    return TimezoneInfo.local();
  }

  now(): DateTime<"local"> {
    const preferredTz = this.tz?.peek();
    if (preferredTz) {
      return DateTime.fromMse(Epoch.currentMse(), Local);
    }
    return Local.now();
  }

  get today(): NaiveDate {
    return this.now().withTime().date;
  }

  get rfc3339(): string {
    return this.info.offset.asSignedHm();
  }

  fromMse(mse: MsSinceEpoch): DateTime<"local"> {
    const preferredTz = this.tz?.peek();
    if (preferredTz) {
      return DateTime.fromMse(mse, Local);
    }
    return Local.fromMse(mse);
  }
}

export const LonaUserLocal = new LonaUserLocalTimezone();

export function hasTimezonePreference(): boolean {
  return LonaUserLocal["tz"]?.peek() !== null;
}
